import { useState } from 'react';
import { ActionIcon, Stack, Textarea } from '@mantine/core';
import { IconSend } from '@tabler/icons-react';

interface EventsComposerProps {
  /** Обработчик отправки текста. Возвращает Promise — composer ждёт его перед очисткой поля. */
  onSubmit: (text: string) => Promise<void> | void;
  placeholder?: string;
}

/**
 * EventsComposer — единый блок ввода для текстового сообщения/комментария.
 *
 * Слой kernel-only: composer ничего не знает о comment-rpc или другой
 * предметной логике. Родитель передаёт `onSubmit`, в котором решает,
 * куда сохранить введённый текст (comments, activity-log и т.п.).
 */
export function EventsComposer({ onSubmit, placeholder }: EventsComposerProps) {
  const [text, setText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      void handleSubmit();
    }
  };

  const handleSubmit = async () => {
    const content = text.trim();
    if (!content || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onSubmit(content);
      setText('');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Stack gap='xs' m='sm'>
      <Textarea
        value={text}
        onChange={e => setText(e.currentTarget.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder ?? 'Добавить комментарий...'}
        minRows={1}
        autosize
        disabled={isSubmitting}
        rightSection={
          <ActionIcon
            component='button'
            type='button'
            variant='filled'
            color='blue'
            size='sm'
            aria-label='Отправить'
            disabled={!text.trim() || isSubmitting}
            onClick={handleSubmit}
          >
            <IconSend size={14} />
          </ActionIcon>
        }
        rightSectionWidth={36}
        styles={{
          input: {
            border: '1px solid var(--mantine-color-gray-3)',
            borderRadius: 8,
            padding: '8px 10px',
          },
          root: {
            flex: 1,
          },
        }}
      />
    </Stack>
  );
}
